import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@mui/material";

//Icons
import KeyboardArrowRightOutlinedIcon from "@mui/icons-material/KeyboardArrowRightOutlined";
import KeyboardArrowLeftOutlinedIcon from "@mui/icons-material/KeyboardArrowLeftOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import ZoomOutMapOutlinedIcon from "@mui/icons-material/ZoomOutMapOutlined";
import ZoomInMapOutlinedIcon from "@mui/icons-material/ZoomInMapOutlined";
import ReorderOutlinedIcon from "@mui/icons-material/ReorderOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import AcquisitionsAPI from "../../api/acquisitions";
import { usePermissions } from "../../hooks/usePermissions";
import { getDateFormat, monolithMoment } from "../../utils/date-format";
import { ItemTotalBadge } from "@/Features";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useInfiniteQuery,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import Table, { Column, useTable } from "../../Monolith-UI/Table/Table";
import ComboButton from "../../Monolith-UI/ComboButton/ComboButton.js";
import { useQueryFilter } from "../../Monolith-UI/QueryFilter/QueryFilter.js";
import Loader from "../../components/Loader.js";
import styled from "styled-components";
import AcquisitionDimensions from "./Components/AcquisitionDimensions";
import { useSnackbar } from "notistack";
import { nanoid } from "nanoid";
import Flyout, { FlyoutHeader } from "../../Monolith-UI/Flyout/Flyout.js";
import TaskButton from "../../Monolith-UI/TaskButton.js";
import synchronizeColumnState from "../../utils/synchronize-column-state.js";
import { addCustomFieldsToColumnDefs } from "../../components/Evidence/CustomFieldCell";
import { getContacts, getEvidence, getSoftwareNames, userApi } from "../../api";
import { useDebounce, useDebouncedCallback } from "use-debounce";
import { MONOLITH_PERMISSIONS } from "../../constants";
import { Input, useTheme } from "@monolith-forensics/monolith-ui";
import CustomFieldsApi from "@/api/custom-fields";
import { storageQueryKeys } from "@/Features/Storage/storage.query";
import { StorageAPI } from "@/Features/Storage/storage.api";
import { CUSTOM_FIELD_CATEGORIES } from "../../pages/Settings/CustomFieldSettings/utils/customFieldConstants";
import { resolveCustomFields } from "@/utils/customFieldFilterResolver";
import CustomAttribute from "@/types/CustomAttribute";

import AcqColumnDefs from "./Components/AcqColumnDefs";

// Import our types
import {
  AcquisitionViewProps,
  AcquisitionQueryState,
  ColumnState,
  UpdateCacheDetails,
  FlyoutContentProps,
  AcquisitionPage,
  FilterDimension,
  Acquisition,
  QueryFilterRef,
  FilterUpdate,
  InfiniteQueryData,
  StatusItem,
} from "./acquisitions.types";
import React from "react";
import {
  AcquisitionDataImportModal,
  CopyAcquisitionModal,
  CreateAcquisitionModal,
  DeleteAcquisitionModal,
  EditAcquisitionModal,
} from "./Modals";
import { UploadIcon } from "lucide-react";
import { useFeatures } from "@/contexts/FeaturesContext";

let columnDefs = AcqColumnDefs;

const AcquisitionView: React.FC<AcquisitionViewProps> = ({
  infoData,
  stateStoreKey = !infoData?.evidence_id
    ? "case:acquisition:view"
    : "evidence:acquisition:view",
}: AcquisitionViewProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = usePermissions();
  const { hasFeatures, FEATURES } = useFeatures();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyFromData, setCopyFromData] = useState<Acquisition | null>(null);
  const [showDataImportModal, setShowDataImportModal] = useState(false);

  const queryFilter = useRef<QueryFilterRef | null>(null);

  const [pageSize, setPageSize] = useState(100);

  const [isFlyoutVisible, setIsFlyoutVisible] = useState(false);
  const [flyoutData, setFlyoutData] = useState<Acquisition | null>(null);

  const table = useTable();

  const [columnState, setColumnState] = useState<ColumnState[]>(() => {
    return synchronizeColumnState(
      columnDefs,
      JSON.parse(localStorage.getItem(stateStoreKey) || "{}")
    );
  });

  const [searchText, setSearchText] = useState<string | null>(
    localStorage.getItem(`${stateStoreKey}:searchText`)
  );
  const [debouncedSearchText] = useDebounce(searchText, 500);

  const currentSort = useMemo(() => {
    let [sort] = columnState
      ?.filter((c) => c.sorting?.active)
      .map((c) => ({ field: c.dataField, sort: c.sorting?.direction }));

    return sort;
  }, [columnState]);

  const evidenceQuery = !infoData.evidence_id
    ? {
        queryKey: [
          "evidence:list",
          {
            case_id: infoData.case_id,
          },
        ],
        queryFn: () =>
          getEvidence({
            case_id: infoData.case_id,
          }),
      }
    : null;

  useQueries({
    queries: [
      {
        queryKey: [
          ...storageQueryKeys.legacyLists,
          {
            case_id: infoData.case_id,
            active_only: true,
            include_general_storage: true,
          },
        ],
        queryFn: () =>
          StorageAPI.getStorage({
            case_id: infoData.case_id,
            active_only: true,
            include_general_storage: true,
          }),
      },
      {
        queryKey: ["acquisition", "software", "list"],
        queryFn: () => getSoftwareNames(),
      },
      {
        queryKey: ["acquisitions:formats:list"],
        queryFn: () => AcquisitionsAPI.getFormats(),
      },
      {
        queryKey: ["acquisitions:type:list"],
        queryFn: () => AcquisitionsAPI.getTypes(),
      },
      {
        queryKey: ["people:contacts", { case_id: infoData.case_id }],
        queryFn: () =>
          getContacts({
            case_id: infoData.case_id,
          }),
      },
      {
        queryKey: ["cases:leads"],
        queryFn: () =>
          userApi.getUsers({
            includeInactive: false,
            includeObservers: false,
          }),
      },
      {
        // Fetch custom fields boxes
        queryKey: ["acquisitions", "customFields", "list"],
        queryFn: () =>
          CustomFieldsApi.getCustomFields({
            category: CUSTOM_FIELD_CATEGORIES.ACQUISITION,
          }),
      },
      ...(evidenceQuery ? [evidenceQuery] : []),
    ],
  });

  const [query, setQuery] = useState<AcquisitionQueryState>({
    query: {
      conditions: [],
      case_id: infoData?.evidence_id ? null : infoData?.case_id,
      evidence_id: infoData?.evidence_id,
      pageSize,
      page: 1,
    },
  });

  const { data, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery<AcquisitionPage>({
      queryKey: [
        "acquisitions:list",
        {
          query: {
            ...query?.query,
            order: query?.query?.order || currentSort,
            pageSize,
          },
        },
      ],
      queryFn: ({ pageParam }) =>
        AcquisitionsAPI.query({
          query: {
            ...query?.query,
            order: query?.query?.order || currentSort,
            pageSize,
            page: pageParam,
          },
        }),
      getNextPageParam: (lastPage) => {
        return lastPage.nextPage;
      },
      getPreviousPageParam: (firstPage) => {
        if (firstPage.page - 1 === 0) return null;
        return firstPage.page - 1;
      },
      initialPageParam: 1,
      enabled: !!query,
      placeholderData: (data) => data,
    });

  const records = data?.pages?.reduce((acc, page) => {
    return [...acc, ...page.data];
  }, [] as Acquisition[]);

  const totalRecords = data?.pages?.[0]?.total || 0;

  const debouncedFetchNextPage = useDebouncedCallback(() => {
    fetchNextPage();
  }, 50);

  const handleActionButtonClick = (rowData: Acquisition) => {
    setIsFlyoutVisible(true);
    setFlyoutData(rowData);
  };
  const handleFilter = (newFilter: FilterUpdate) => {
    setQuery((q) => {
      return { query: { ...q?.query, ...newFilter, page: 1 } };
    });
  };
  const handleColumnVisibility = (column: ColumnState, visible: boolean) => {
    setColumnState((cs) => {
      return cs.map((c) => {
        if (c.dataField === column.dataField) {
          return {
            ...c,
            visible,
          };
        }
        return c;
      });
    });
  };
  const handleColumnReorder = (
    newOrder: { column: string; order: number }[]
  ) => {
    setColumnState((cs) => {
      return newOrder.map((o) => {
        const foundColumn = cs.find((c) => c.dataField === o.column);
        return {
          ...foundColumn,
          dataField: foundColumn?.dataField || o.column,
          caption: foundColumn?.caption || "",
          order: o.order,
        } as ColumnState;
      });
    });
  };
  const handleColumnResize = (e: {
    columns: { dataField: string; width: string }[];
  }) => {
    setColumnState((cs) => {
      return cs.map((c) => {
        const col = e.columns.find((col) => col.dataField === c.dataField);
        if (col) {
          return {
            ...c,
            width: parseInt(col.width.replace(/px/g, "")),
          };
        }
        return c;
      });
    });
  };
  const handleExportTable = () => {
    // show snackbar
    enqueueSnackbar("Exporting evidence table...", {
      variant: "info",
    });

    AcquisitionsAPI.exportAcquisitionList({
      query: {
        ...query?.query,
        order: query?.query?.order || currentSort,
        pageSize,
      },
      type: "xlsx",
      columns: columnState
        .filter((c) => c.visible !== false)
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((c) => {
          return { header: c.caption, ...c };
        }),
      date_format: getDateFormat({
        isMoment: true,
        includeTime: true,
      }),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      custom_date_format: getDateFormat({
        isMoment: true,
        includeTime: false,
      }),
    }).then((res) => {
      const { signedUrl, filename } = res;
      const el = document.createElement("a");
      el.href = signedUrl.replace(
        "http://localhost:3000",
        "http://localhost:3001"
      );
      el.download = filename;
      el.click();

      // remove element
      el.remove();

      // remove snackbar
    });
  };
  const handleGetNextItem = (acq_id: number) => {
    const current = records?.findIndex((i) => i.acq_id === acq_id);
    const nextItem = records?.[current! + 1] || records?.[0];

    if (nextItem) {
      setFlyoutData(nextItem);
    }
  };
  const handleGetPrevItem = (acq_id: number) => {
    const current = records?.findIndex((i) => i.acq_id === acq_id);
    const prevItem = records?.[current! - 1] || records?.[records.length - 1];

    if (prevItem) {
      setFlyoutData(prevItem);
    }
  };
  const handleReload = () => {
    refetch();
  };
  const handleClearFilters = () => {
    queryFilter.current?.clear();
  };
  const handleSort = (field: string) => {
    const savedColumn = columnState?.find((svc) => field === svc.dataField);
    const order = savedColumn?.sorting?.direction || null;

    let newOrder: { field: string; sort: "asc" | "desc" } | null = null;

    // if sorted on a different column, reset the sort
    if (query?.query?.order?.field !== field) {
      newOrder = {
        field,
        sort: "asc",
      };
    }

    // otherwise rotate the sort options on the current column
    else {
      switch (order) {
        case "asc":
          newOrder = {
            field,
            sort: "desc",
          };
          break;
        case "desc":
          newOrder = null;
          break;
        default:
          newOrder = {
            field,
            sort: "asc",
          };
      }
    }

    let newCols = columnState?.map((c) => {
      if (c.dataField === field) {
        return {
          ...c,
          sorting: {
            active: newOrder ? true : false,
            direction: newOrder?.sort,
          },
        };
      }
      const { sorting, ...rest } = c;
      return rest;
    });

    let newQuery = {
      query: {
        ...query.query,
        order: newOrder,
      },
    };

    setColumnState(newCols);
    setQuery(newQuery);
  };
  const onAcquisitionCreated = () => {
    refetch();
  };
  const onAcquisitionEdited = () => {
    refetch();

    if (flyoutData) {
      queryClient
        .refetchQueries({
          queryKey: [
            "acquisition",
            { acq_id: parseInt(flyoutData.acq_id.toString(), 10) },
          ],
        })
        .then(() => {
          const acqData = queryClient.getQueryData([
            "acquisition",
            { acq_id: flyoutData.acq_id },
          ]) as Acquisition[] | undefined;

          if (acqData?.[0]) {
            setFlyoutData(acqData[0]);
          }
        });
    }
  };
  const onAcquisitionDeleted = () => {
    setIsFlyoutVisible(false);
    setFlyoutData(null);
    refetch();
  };
  const handleSelect = (item: string) => {
    if (item === "import") {
      navigate(`/cases/${infoData.case_id}/acquisitions?v=import`);
    }
  };
  // Detect scroll to bottom
  const handleScroll = (e: React.UIEvent<HTMLElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const pagesLength = data?.pages?.length || 0;
    if (scrollHeight - scrollTop <= clientHeight + 100 * pagesLength) {
      if (hasNextPage && !isFetchingNextPage) {
        debouncedFetchNextPage();
      }
    }
  };
  const updateCache = (
    updateDetails: UpdateCacheDetails = { rowData: {}, updateData: {} }
  ) => {
    const itemUUID = updateDetails.rowData.uuid;
    queryClient.setQueryData(
      [
        "acquisitions:list",
        {
          query: {
            ...query?.query,
            order: query?.query?.order || currentSort,
            pageSize,
          },
        },
      ],
      (data: InfiniteQueryData | undefined) => {
        if (!data) return null;
        const newPages = data?.pages.map((page: AcquisitionPage) => {
          return {
            ...page,
            data: page?.data?.map((item) => {
              if (item.uuid === itemUUID) {
                return {
                  ...item,
                  ...updateDetails.updateData,
                };
              }
              return item;
            }),
          };
        });

        return {
          ...data,
          pages: newPages,
        };
      }
    );
  };

  const { data: customFields } = useQuery({
    queryKey: ["acquisitions", "customFields", "list"],
    queryFn: () =>
      CustomFieldsApi.getCustomFields({
        category: CUSTOM_FIELD_CATEGORIES.ACQUISITION,
      }),
  });

  useEffect(() => {
    const acqID = searchParams.get("acq_id");

    if (acqID) {
      setFlyoutData({ acq_id: parseInt(acqID, 10) } as Acquisition);
      setIsFlyoutVisible(true);
    }
  }, [searchParams]);

  useEffect(() => {
    let oldState = JSON.parse(localStorage.getItem(stateStoreKey) || "{}");
    localStorage.setItem(
      stateStoreKey,
      JSON.stringify({
        ...oldState,
        cols: columnState,
      })
    );
  }, [columnState, stateStoreKey]);

  useEffect(() => {
    if (!customFields) return;
    // Add custom fields to column defs
    setColumnState((cs) => {
      return addCustomFieldsToColumnDefs(customFields, cs);
    });
  }, [customFields]);

  let filterDimensions: FilterDimension[] = AcquisitionDimensions;

  // update filter dimensions with custom fields
  filterDimensions = useMemo(() => {
    if (customFields) {
      //update filter dimensions
      return [...AcquisitionDimensions, ...resolveCustomFields(customFields)];
    }
    return AcquisitionDimensions;
  }, [customFields]);

  const { queryButton, conditions } = useQueryFilter({
    dimensions: filterDimensions.sort((a, b) => a.name.localeCompare(b.name)),
    onQuerySet: handleFilter,
    queryFilter: queryFilter,
    stateStoring: {
      enabled: true,
      type: "localStorage",
      storageKey: stateStoreKey,
    },
  });

  useEffect(() => {
    setQuery((q) => ({
      query: {
        ...q?.query,
        search: debouncedSearchText ? debouncedSearchText : null,
        page: 1,
      },
    }));
  }, [debouncedSearchText]);

  return (
    <>
      <div
        style={{
          display: "flex",
          flex: "initial",
          flexDirection: "row",
          alignContent: "center",
          alignItems: "center",
          marginBottom: 5,
          marginTop: 5,
          overflow: "hidden",
        }}
      >
        <Button
          size="small"
          variant="contained"
          color="primary"
          disabled={!hasPermission()}
          data-testid="acquisitions-new-button"
          onClick={(e) => {
            setShowCreateModal(true);
          }}
          style={{
            minWidth: "fit-content",
            fontSize: 11,
            padding: "3px 6px",
          }}
        >
          + New Acquisition
        </Button>
        <div style={{ marginLeft: 10 }}>{queryButton}</div>
        <ItemTotalBadge total={totalRecords || 0} />
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            alignContent: "center",
            alignItems: "center",
            minWidth: "fit-content",
          }}
        >
          {!hasFeatures([FEATURES.ACQUISITIONS_CSV_IMPORT]) &&
          hasPermission(MONOLITH_PERMISSIONS.ACQUISITIONS_BULK_IMPORT) &&
          !infoData?.evidence_id ? (
            <ComboButton
              type="dropDown"
              data={["Import from CSV"]}
              title={"More Actions"}
              showDropdownIcon={true}
              variant="outlined"
              useSelectMode={false}
              onItemSelect={() => handleSelect("import")}
              textColor={theme.palette.text.primary}
              selectedItems={[]}
            >
              Actions
            </ComboButton>
          ) : (
            ""
          )}
          {hasFeatures([FEATURES.ACQUISITIONS_CSV_IMPORT]) &&
            hasPermission(MONOLITH_PERMISSIONS.ACQUISITIONS_BULK_IMPORT) &&
            !infoData?.evidence_id && (
              <ComboButton
                type="button"
                variant="outlined"
                textColor={theme.palette.text.secondary}
                title="Import from CSV"
                onClick={() => setShowDataImportModal(true)}
                data={[]}
                selectedItems={[]}
              >
                <UploadIcon size={14} />
              </ComboButton>
            )}
          <ComboButton
            type="multi-select"
            data={columnState.filter((c) => c.showInColumnChooser !== false)}
            displayField="caption"
            idField={"dataField"}
            selectedItems={columnState.filter((c) => c.visible !== false)}
            variant="outlined"
            closeOnSelect={false}
            showSearch={true}
            dropDownTitle={() => {
              return (
                <div
                  style={{
                    margin: "5px 0px",
                    padding: 3,
                    color: theme.palette.text.secondary,
                    display: "flex",
                    alignItems: "center",
                    minWidth: 200,
                  }}
                >
                  Select Columns
                </div>
              );
            }}
            onItemDeSelect={(item) => {
              handleColumnVisibility(item, false);
            }}
            onItemSelect={(item) => {
              handleColumnVisibility(item, true);
            }}
            textColor={theme.palette.text.secondary}
            title={"Select Columns"}
          >
            <ViewColumnOutlinedIcon style={{ fontSize: 18 }} />
          </ComboButton>
          <ComboButton
            type="button"
            variant="outlined"
            textColor={theme.palette.text.secondary}
            title={"Export Table"}
            onClick={handleExportTable}
            data={[]}
            selectedItems={[]}
          >
            <FileDownloadOutlinedIcon style={{ fontSize: 18 }} />
          </ComboButton>
          <ComboButton
            type="button"
            variant="outlined"
            textColor={theme.palette.text.secondary}
            title={"Clear Filters"}
            onClick={handleClearFilters}
            data={[]}
            selectedItems={[]}
          >
            <FilterListOffIcon style={{ fontSize: 18 }} />
          </ComboButton>
          <ComboButton
            type="button"
            variant="outlined"
            textColor={theme.palette.text.secondary}
            title={table.isCompact ? "Zoom In" : "Zoom Out"}
            onClick={() => table.toggleCompact()}
            data={[]}
            selectedItems={[]}
          >
            {table.isCompact && (
              <ZoomOutMapOutlinedIcon style={{ fontSize: 18 }} />
            )}
            {!table.isCompact && (
              <ZoomInMapOutlinedIcon style={{ fontSize: 18 }} />
            )}
          </ComboButton>
          <ComboButton
            type="button"
            variant={"outlined"}
            textColor={
              table.isStriped
                ? theme.palette.primary.main
                : theme.palette.text.secondary
            }
            title={table.isStriped ? "Hide Stripes" : "Show Stripes"}
            onClick={() => table.toggleStripes()}
            data={[]}
            selectedItems={[]}
          >
            <ReorderOutlinedIcon style={{ fontSize: 18 }} />
          </ComboButton>
          <Input
            placeholder="Search Acquisitions"
            variant="outlined"
            height={30}
            defaultValue={searchText || ""}
            onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) => {
              const currentValue = (e.target as HTMLInputElement).value.trim();
              setSearchText(currentValue);

              localStorage.setItem(`${stateStoreKey}:searchText`, currentValue);
            }}
            style={{
              marginLeft: "10px",
              border: searchText
                ? `1px solid ${theme.palette.primary.main}`
                : "",
            }}
          />
        </div>
      </div>
      {records && (
        <>
          {<div>{conditions}</div>}
          <Table
            data={records || []}
            reloadFn={handleReload}
            totalRecords={totalRecords || 0}
            tableInstance={table}
            keyValue="acq_id"
            columnProps={{ minWidth: 150, width: 150 }}
            queryKey={[
              "acquisitions:list",
              {
                query: {
                  ...query?.query,
                  order: query?.query?.order || currentSort,
                  pageSize,
                },
              },
            ]}
            onHeaderClick={(col: ColumnState) =>
              col?.sorting?.enabled === false ? null : handleSort(col.dataField)
            }
            onRowUpdated={updateCache}
            onScroll={handleScroll}
            onColumnReorder={handleColumnReorder}
            onColumnResize={handleColumnResize}
            onActionButtonClick={handleActionButtonClick}
            showActionColumn={true}
            focusedRow={flyoutData}
          >
            {columnState.map((col) => {
              return <Column key={col.dataField} {...col} />;
            })}
          </Table>
        </>
      )}
      {!records && <Loader message={"Retrieving Acquisitions..."} />}
      {isFlyoutVisible && flyoutData && (
        <Flyout
          onClose={() => {
            setIsFlyoutVisible(false);
            setFlyoutData(null);
          }}
        >
          <FlyoutHeader>
            <div style={{ width: "100%" }}>
              <h4
                className="flyout-title"
                style={{
                  margin: "5px 0px",
                  color: theme.palette.text.primary,
                }}
              >
                {flyoutData.acq_name || "..."}
              </h4>
              <div
                style={{
                  display: "flex",
                  alignContent: "center",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    color: theme.palette.text.secondary,
                    fontSize: 12,
                  }}
                >
                  Acquisition Details
                </div>
                <div
                  style={{
                    marginLeft: "auto",
                    display: "flex",
                    alignContent: "center",
                    alignItems: "center",
                  }}
                >
                  <TaskButton
                    onClick={() => handleGetPrevItem(flyoutData.acq_id)}
                    style={{
                      fontSize: 10,
                    }}
                    variant="outlined"
                  >
                    <KeyboardArrowLeftOutlinedIcon style={{ fontSize: 17 }} />
                  </TaskButton>
                  <TaskButton
                    onClick={() => handleGetNextItem(flyoutData.acq_id)}
                    style={{
                      fontSize: 10,
                    }}
                    variant="outlined"
                  >
                    <KeyboardArrowRightOutlinedIcon style={{ fontSize: 17 }} />
                  </TaskButton>
                </div>
              </div>
            </div>
          </FlyoutHeader>
          <FlyoutContent
            defaultInfo={flyoutData}
            setShowCreateModal={setShowCreateModal}
            setShowCopyModal={setShowCopyModal}
            setShowEditModal={setShowEditModal}
            setShowDeleteModal={setShowDeleteModal}
            setIsFlyoutVisible={setIsFlyoutVisible}
            setFlyoutData={setFlyoutData}
            setCopyFromData={setCopyFromData}
            reloadFn={handleReload}
            columnDefs={columnState}
          />
        </Flyout>
      )}
      <CreateAcquisitionModal
        show={showCreateModal}
        onSubmit={onAcquisitionCreated}
        onClose={() => {
          setShowCreateModal(false);
        }}
        onCancel={() => {
          setShowCreateModal(false);
        }}
        caseInfo={{ case_id: infoData.case_id }}
        evidenceInfo={
          infoData?.evidence_id
            ? { evidence_id: infoData.evidence_id, case_id: infoData.case_id }
            : undefined
        }
      />
      <CopyAcquisitionModal
        show={showCopyModal}
        onSubmit={onAcquisitionCreated}
        onClose={() => {
          setShowCopyModal(false);
          setCopyFromData(null);
        }}
        onCancel={() => {
          setShowCreateModal(false);
          setCopyFromData(null);
        }}
        copyFromData={copyFromData || undefined}
        caseInfo={{ case_id: infoData.case_id }}
        evidenceInfo={
          infoData?.evidence_id
            ? { evidence_id: infoData.evidence_id, case_id: infoData.case_id }
            : undefined
        }
      />
      {flyoutData && (
        <EditAcquisitionModal
          show={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSubmit={onAcquisitionEdited}
          onCancel={() => setShowEditModal(false)}
          defaultFormData={flyoutData}
          caseInfo={{ case_id: infoData.case_id }}
          evidenceInfo={
            infoData?.evidence_id
              ? { evidence_id: infoData.evidence_id, case_id: infoData.case_id }
              : undefined
          }
        />
      )}
      {flyoutData && (
        <DeleteAcquisitionModal
          show={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onDelete={onAcquisitionDeleted}
          defaultData={flyoutData}
        />
      )}
      {showDataImportModal && (
        <AcquisitionDataImportModal
          open={showDataImportModal}
          onClose={() => setShowDataImportModal(false)}
          onComplete={() => refetch()}
          caseId={infoData.case_id}
        />
      )}
    </>
  );
};

const FlyoutContent = styled(
  ({
    className,
    defaultInfo,
    setShowCopyModal,
    setShowEditModal,
    setShowDeleteModal,
    setCopyFromData,
    reloadFn,
    columnDefs,
  }: FlyoutContentProps & { className?: string }) => {
    const { hasPermission } = usePermissions();
    const theme = useTheme();
    const queryClient = useQueryClient();

    const { data: itemData } = useQuery({
      queryKey: [
        "acquisition",
        { acq_id: parseInt(defaultInfo.acq_id.toString(), 10) },
      ],
      queryFn: () =>
        AcquisitionsAPI.getAcquisitions({
          acq_id: parseInt(defaultInfo.acq_id.toString(), 10),
        }),
      initialData: [defaultInfo],
      select: (data) => data[0],
    });

    const hasAcqId = itemData.acq_id !== undefined && itemData.acq_id !== null;

    const title = document.querySelector(".flyout-title") as HTMLElement;
    if (title) title.innerText = itemData.acq_name || defaultInfo.acq_name;

    const handleCopy = () => {
      setCopyFromData(itemData);
      setShowCopyModal(true);
    };

    const handleEdit = () => {
      setShowEditModal(true);
    };

    const handleDelete = () => {
      setShowDeleteModal(true);
    };

    const handleChange = (data: StatusItem) => {
      reloadFn();
      queryClient.setQueryData(
        ["acquisition", { acq_id: itemData.acq_id }],
        (oldData: unknown) => {
          const dataObj = (oldData as Acquisition[])?.[0] || {};
          return [{ ...dataObj, status: data.status }];
        }
      );
    };

    const dataFieldIgnoreList = ["acq_notes"];

    const data = {
      ...itemData,
      ...(itemData?.custom_attributes?.reduce(
        (acc: Record<string, any>, attr: CustomAttribute) => {
          acc[`custom_field_${attr.field_id}`] = attr.value;
          return acc;
        },
        {}
      ) || {}),
    };

    const detail = (
      <div className="detail-container">
        {columnDefs
          .filter((c) => !dataFieldIgnoreList.includes(c.dataField))
          .filter((c) =>
            c.dataField.includes("custom_field_") ? !!data[c.dataField] : true
          )
          .map((c) => {
            return (
              <div className="detail-item" key={nanoid()}>
                <div className="detail-label">{c.caption}</div>
                {c.render ? (
                  c.render(data, { onChange: handleChange })
                ) : c.dataType === "date" ? (
                  <>
                    {monolithMoment({
                      timestamp: data[c.dataField],
                      includeTime: true,
                    })}
                  </>
                ) : (
                  <div className="detail-value">{data[c.dataField]}</div>
                )}
              </div>
            );
          })}
      </div>
    );

    return (
      <div className={className}>
        {hasAcqId && (
          <>
            <div className="action-menu">
              {hasPermission() && (
                <>
                  <div className="action-menu-item" onClick={handleCopy}>
                    <ContentCopyIcon
                      style={{ color: theme.palette.primary.main }}
                    />
                    <div className="action-menu-label">Copy</div>
                  </div>
                  <div className="action-menu-item" onClick={handleEdit}>
                    <EditOutlinedIcon
                      style={{ color: theme.palette.primary.main }}
                    />
                    <div className="action-menu-label">Edit</div>
                  </div>
                  <div className="action-menu-item" onClick={handleDelete}>
                    <DeleteOutlineOutlinedIcon
                      style={{ color: theme.palette.error.main }}
                    />
                    <div className="action-menu-label">Delete</div>
                  </div>
                </>
              )}
            </div>
            {detail}
            <div className="detail-label" style={{ marginTop: 30 }}>
              Description
            </div>
            <div style={{ position: "relative" }}>
              <div className="item-description">{itemData.acq_notes}</div>
            </div>
          </>
        )}
        {!hasAcqId && <Loader message={"Loading..."} />}
      </div>
    );
  }
)`
  padding: 20px;
  & {
    max-height: calc(100vh - 100px);
    overflow-y: auto;
    margin-right: 5px;
  }
  & .action-menu {
    display: flex;
    align-items: center;
    align-content: center;
    margin-bottom: 15px;
    margin-right: 10px;
  }
  & .action-menu-item {
    display: flex;
    align-items: center;
    align-content: center;
    margin-right: 12px;
    padding: 3px;
    cursor: pointer;
    border-radius: 5px;
    font-size: 10px;
    &:hover {
      background-color: ${({ theme }) => theme.palette.action.hover};
    }
    & svg {
      font-size: 15px;
      margin-right: 5px;
    }
  }
  & .detail-item {
    display: flex;
    align-items: center;
    align-content: center;
    margin: 10px 0px;
    font-size: 12px;
  }
  & .detail-label {
    color: ${({ theme }) => theme.palette.text.secondary};
    margin-right: 10px;
    min-width: 150px;
  }
  & .detail-value {
    color: ${({ theme }) => theme.palette.text.primary};
  }
  & .item-description {
    position: relative;
    resize: none;
    margin-top: 10px;
    font-size: 12px;
    font-family: ${({ theme }) => theme.typography.fontFamily};
    color: ${({ theme }) => theme.palette.text.primary};
    background-color: transparent;
    white-space: pre-wrap;
    width: 100%;
    height: fit-content;
    max-height: 30%;
    border: 1px solid transparent;
    border-radius: 5px;
    padding: 10px;
    transition: border 0.2s ease-in-out;
    outline: none;
    &:hover {
      border: 1px solid ${({ theme }) => theme.palette.divider};
    }
    &:focus {
      border: 1px solid ${({ theme }) => theme.palette.primary.main};
    }
  }
  & .item-description-placeholder {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    color: ${({ theme }) => theme.palette.divider};
    display: flex;
    alignitems: center;
    fontsize: 12px;
    padding: 10px;
  }
`;

export default AcquisitionView;
